import { pvpRosterSize } from '@kenocyber/shared';

/**
 * Numbers as they appear INSIDE the arena: damage popups and enemy health.
 *
 * Brazilian Portuguese throughout - comma for decimals, and the short scale
 * spelled the way a player reads it aloud ("mil", "mi"). Kept separate from
 * the HUD formatter because the constraints are different: these are drawn
 * over moving sprites at small sizes, so they must stay short and never wide.
 */

const THOUSAND = 1_000;
const MILLION = 1_000_000;
const BILLION = 1_000_000_000;
const TRILLION = 1_000_000_000_000;

/** Trims a fixed-decimal string: 1,00 -> 1; 1,20 -> 1,2; 1,25 stays. */
function trim(value: string): string {
  return value.replace(/,?0+$/, '').replace(/,$/, '');
}

function withComma(value: number, decimals: number): string {
  return trim(value.toFixed(decimals).replace('.', ','));
}

/**
 * A combat number: damage dealt, health remaining.
 *
 * Never returns an em dash. A dash means "there is no measurement yet", which
 * is a legitimate thing for a rolling metric to say and a nonsensical thing
 * for a hit that just landed - a hit always has a value, even a small one.
 * Anything not finite is clamped to zero rather than hidden.
 */
export function formatCombatNumber(value: number): string {
  if (!Number.isFinite(value)) {
    return '0';
  }
  const magnitude = Math.abs(value);
  if (magnitude >= TRILLION) {
    return `${withComma(value / TRILLION, 1)} tri`;
  }
  if (magnitude >= BILLION) {
    return `${withComma(value / BILLION, 1)} bi`;
  }
  if (magnitude >= MILLION) {
    return `${withComma(value / MILLION, 1)} mi`;
  }
  if (magnitude >= THOUSAND) {
    return `${withComma(value / THOUSAND, 1)} mil`;
  }
  // Below a thousand the exact figure fits, and small damage values really do
  // differ in the hundredths - 1,01 is a different shot from 0,98.
  return withComma(value, 2);
}

/**
 * **O estilo de um número de combate, declarado UMA vez.**
 *
 * A caça tinha-o embutido na sua cena e o duelo não tinha número nenhum. O
 * `+VALOR` do Roubo de Vida precisa de aparecer nos dois, e o contrato manda
 * preservar a mesma fonte, tamanho, contorno e animação - portanto o estilo
 * saiu de uma cena para aqui, onde as duas o leem.
 *
 * Uma constante partilhada e não duas iguais: duas seriam duas coisas que podem
 * divergir, e a que divergisse seria a que ninguém abriu.
 */
export const COMBAT_TEXT_FONT_PX = 15;

export const COMBAT_TEXT_STYLE = {
  fontStyle: '700',
  fontSize: `${COMBAT_TEXT_FONT_PX}px`,
  fontFamily: '"Segoe UI", "Inter", system-ui, sans-serif',
  stroke: '#04040a',
  strokeThickness: 3,
} as const;

/** A cor de um número de DANO. */
export const COMBAT_TEXT_DAMAGE_COLOR = '#ffffff';

/**
 * A cor de um `+VALOR` de cura (gate V28G.I).
 *
 * **A única coisa que distingue este número de um de dano** - fonte, tamanho,
 * contorno, subida e duração são os mesmos. Aprovada pelo proprietário como
 * verde-néon, depois de a folha visual mostrar as duas hipóteses no quadro real.
 *
 * NÃO é o verde reservado à futura cura de aliados: aquele é uma decisão por
 * tomar sobre um efeito que ainda não existe, e este é texto. Se a escolha for
 * outra, muda-se aqui e em mais lado nenhum - as duas cenas, o censo da bancada
 * e as provas leem TODAS esta constante, e nenhuma repete o valor.
 */
export const COMBAT_TEXT_HEAL_COLOR = '#39ff14';

/**
 * A cor de um número de DETONAÇÃO de sangramento (gate V28G.N).
 *
 * Carmesim, e é a terceira cor deste ficheiro por uma razão de leitura: o dano
 * comum é branco, a cura é verde-néon, e um número que sai da nave no instante
 * em que as marcas rompem tem de se ler como sendo DAQUELE golpe e não do tiro
 * que aconteceu no mesmo tique.
 *
 * O mesmo carmesim do 傷 e das garras, e é deliberado: o número, a marca e o
 * rasgo são a mesma joia a falar, e três vermelhos diferentes seriam três
 * coisas.
 */
export const COMBAT_TEXT_BLEED_COLOR = '#ff2d4a';

/**
 * **Quantas vezes maior do que um número de dano comum: 2,4×.**
 *
 * ## Porque não é o mesmo tamanho
 *
 * Porque não é a mesma coisa. Um número de dano comum é um tiro entre muitos;
 * este é a soma de tudo o que dez, vinte ou trinta marcas guardaram, cobrada de
 * uma vez, e sai no MESMO tique em que os tiros normais também estão a imprimir
 * os seus. A quinze píxeis ele desaparecia no meio deles - o dono viu-o no ecrã
 * e disse que estava pequeno demais.
 *
 * Duas vírgula quatro, e não duas: o pedido foi «pelo menos duas vezes maior», e
 * uma margem acima do mínimo pedido é o que faz a diferença ler-se como
 * deliberada e não como um acaso de arredondamento.
 *
 * DERIVADO de {@link COMBAT_TEXT_FONT_PX}, e é isso que mantém a promessa
 * verdadeira: mudar o tamanho do número comum move este com ele, e a relação
 * «pelo menos o dobro» não pode ser quebrada por uma edição noutro sítio.
 */
export const COMBAT_TEXT_BLEED_SCALE = 2.4;

/** O tamanho, em píxeis, de um número de detonação. */
export const COMBAT_TEXT_BLEED_FONT_PX = Math.round(COMBAT_TEXT_FONT_PX * COMBAT_TEXT_BLEED_SCALE);

/**
 * **O estilo de um número de DETONAÇÃO: carmesim néon.**
 *
 * ## O que faz o néon, e o que NÃO faz
 *
 * Uma sombra sem deslocamento, da cor do próprio número, desfocada e aplicada ao
 * preenchimento E ao contorno. É isso um halo: a cor a sangrar para fora da
 * forma, como um tubo de néon sangra para a parede atrás dele. Não é um segundo
 * texto por baixo, não é um `Graphics`, não é um `postFX` - é uma propriedade do
 * mesmo objeto, portanto não custa um objeto nem um passe de render.
 *
 * O contorno escuro fica, e mais grosso: sem ele o halo encosta ao carmesim do
 * preenchimento e o algarismo perde a borda. O escuro é o que separa a luz da
 * forma.
 */
export const COMBAT_TEXT_BLEED_STYLE = {
  /*
   * **Inclinado e de peso médio, e não preto.**
   *
   * A 900 com um contorno de cinco píxeis, o número lia-se gordo: os traços
   * engrossavam até os contra-formas dos algarismos fecharem, e o dono viu-o e
   * disse-o. Peso é a maneira preguiçosa de fazer um número gritar.
   *
   * O que grita sem engordar é a FORMA: estreita, inclinada e de traço uniforme.
   * A inclinação é o que lhe dá direção - um número torto lê-se como um golpe, e
   * um número a prumo lê-se como uma etiqueta.
   */
  fontStyle: 'italic 600',
  fontSize: `${COMBAT_TEXT_BLEED_FONT_PX}px`,
  /*
   * Uma condensada técnica, e as alternativas por ordem de quem a tem.
   *
   * `Bahnschrift` vem com o Windows desde a 1709 e é um grotesco estreito de
   * inspiração DIN - sinalética industrial, traço de espessura constante,
   * terminações a direito. É agressiva pela geometria e não pela gordura.
   * `Arial Narrow` cobre quem não a tenha, e a `Segoe UI` é o último recurso,
   * que é a fonte de todo o resto do jogo.
   *
   * Nenhuma fonte é descarregada: este projeto não tem `@font-face` nenhum, e
   * acrescentar um por causa de um número seria pagar uma rede por um efeito.
   */
  fontFamily: '"Bahnschrift Condensed", "Bahnschrift", "Arial Narrow", "Segoe UI", sans-serif',
  color: COMBAT_TEXT_BLEED_COLOR,
  stroke: '#1a0008',
  /*
   * Três, e não cinco. O contorno separa o algarismo do halo; a cinco ele
   * deixava de separar e passava a ENGROSSAR, que é metade do que o fazia
   * parecer gordo.
   */
  strokeThickness: 3,
  shadow: {
    offsetX: 0,
    offsetY: 0,
    color: COMBAT_TEXT_BLEED_COLOR,
    blur: 16,
    stroke: true,
    fill: true,
  },
} as const;

/**
 * A cor de um número cuja origem é uma REFLEXÃO (gate V28G.O).
 *
 * Branco-neon, e é a quarta cor deste ficheiro. O dano comum é branco, a cura é
 * verde-néon, a detonação é carmesim - e este é branco outra vez, de propósito:
 * o que a reflexão devolve **é o dano do atacante**, virado, e pintá-lo de outra
 * cor faria parecer que a joia inventou dano novo. Ela não inventa nada.
 *
 * O que o distingue de um número de dano comum não é a cor: é a etiqueta
 * `REFLECT` por cima, e o halo que os dois partilham.
 */
export const COMBAT_TEXT_REFLECT_COLOR = '#ffffff';

/** A palavra, escrita uma vez. O contrato fixa-a em maiúsculas e em inglês. */
export const COMBAT_TEXT_REFLECT_LABEL = 'REFLECT';

/**
 * **O dobro de um número de dano comum: 2×** (gate V28G.O.2).
 *
 * ## Porque cresceu
 *
 * Porque não se via. O feixe e o brilho foram desenhados mil vezes fora da
 * arena durante um gate inteiro, e quando a geometria foi corrigida e a
 * composição apareceu pela primeira vez, o dono olhou para ela e disse que o
 * indicador tinha de ser duas vezes maior.
 *
 * A quinze píxeis ela lê-se num número de dano da caça, que nasce sobre um mob
 * a poucos píxeis do rato. Aqui ela nasce sobre uma nave a atravessar uma arena
 * de 4 800 unidades, numa janela de 580 ms, e ao lado de um feixe branco a
 * cortar o ecrã - e perde para tudo isso.
 *
 * ## Uma escala PRÓPRIA, e não {@link COMBAT_TEXT_FONT_PX} mexido
 *
 * Aquela constante é partilhada pelos números de dano da caça e pelo `+TOTAL`
 * verde das duas cenas. Dobrá-la dobrava três sistemas para servir um, e os
 * outros dois estão aprovados.
 *
 * É o mesmo padrão de {@link COMBAT_TEXT_BLEED_SCALE}: uma razão declarada, e o
 * tamanho derivado dela. Mudar o número comum continua a mover este com ele, e
 * a relação «o dobro» não pode ser quebrada por uma edição noutro sítio.
 */
export const COMBAT_TEXT_REFLECT_SCALE = 2;

/** O tamanho do NÚMERO, em píxeis. Derivado da escala. */
export const COMBAT_TEXT_REFLECT_VALUE_PX = Math.round(
  COMBAT_TEXT_FONT_PX * COMBAT_TEXT_REFLECT_SCALE,
);

/**
 * **O tamanho da etiqueta: 60 % do NÚMERO.**
 *
 * A razão não mudou - o que mudou foi o número de que ela deriva. `REFLECT`
 * continua a ser a metade pequena da composição, e continua a sê-lo por
 * construção: ela mede sempre seis décimos do valor, seja qual for o valor.
 *
 * Sessenta por cento e não menos: abaixo disso a palavra deixa de se ler a
 * tamanho de arena, e uma etiqueta ilegível é uma etiqueta que não está lá.
 */
export const COMBAT_TEXT_REFLECT_LABEL_PX = Math.round(COMBAT_TEXT_REFLECT_VALUE_PX * 0.6);

/**
 * **O halo, partilhado pelas duas metades da composição.**
 *
 * Uma sombra sem deslocamento, da cor do próprio texto, desfocada e aplicada ao
 * preenchimento E ao contorno - a mesma técnica do número de detonação, e pela
 * mesma razão: é isso um neon, a cor a sangrar para fora da forma.
 *
 * O desfoque é MENOR do que o da detonação (16). Este número não é um golpe
 * acumulado a rebentar - é um tiro devolvido - e um halo grande fá-lo-ia gritar
 * mais alto do que o dano que ele representa.
 */
const REFLECT_GLOW = Object.freeze({
  offsetX: 0,
  offsetY: 0,
  color: COMBAT_TEXT_REFLECT_COLOR,
  /*
   * DERIVADO da escala, e não um nove fixo (gate V28G.O.2).
   *
   * Nove foi escolhido contra um glifo de quinze píxeis. Contra um de trinta,
   * nove seria metade do halo que a composição tinha - e o néon é precisamente a
   * razão entre a luz e a forma. O desfoque cresce com o que ele envolve.
   */
  blur: 9 * COMBAT_TEXT_REFLECT_SCALE,
  stroke: true,
  fill: true,
});

/**
 * O estilo da ETIQUETA. Pequena, condensada e com o halo.
 *
 * Condensada pela mesma razão que a detonação é: a forma é o que faz uma
 * palavra pequena ler-se, e engordá-la só a tornaria uma mancha.
 */
export const COMBAT_TEXT_REFLECT_LABEL_STYLE = {
  fontStyle: '700',
  fontSize: `${COMBAT_TEXT_REFLECT_LABEL_PX}px`,
  fontFamily: '"Bahnschrift Condensed", "Bahnschrift", "Arial Narrow", "Segoe UI", sans-serif',
  color: COMBAT_TEXT_REFLECT_COLOR,
  stroke: '#04040a',
  strokeThickness: 2,
  shadow: REFLECT_GLOW,
} as const;

/**
 * O estilo do VALOR: o número de dano de sempre, com o halo por cima.
 *
 * Fonte, tamanho e contorno são os de {@link COMBAT_TEXT_STYLE} - o contrato
 * manda usar o formatador de combate existente, e usar o formatador e depois
 * desenhar noutra fonte seria cumprir metade. O que muda é o halo, que é o que
 * liga visualmente o número à etiqueta em cima dele.
 */
export const COMBAT_TEXT_REFLECT_VALUE_STYLE = {
  ...COMBAT_TEXT_STYLE,
  /*
   * O tamanho é o ÚNICO campo que não vem de {@link COMBAT_TEXT_STYLE}.
   *
   * Fonte, peso e contorno continuam a ser os do número de combate - o contrato
   * do gate V28G.O manda usar o formatador existente, e usá-lo noutra letra
   * seria cumprir metade. O que muda é a escala, e o halo que a acompanha.
   */
  fontSize: `${COMBAT_TEXT_REFLECT_VALUE_PX}px`,
  color: COMBAT_TEXT_REFLECT_COLOR,
  shadow: REFLECT_GLOW,
} as const;

/**
 * O espaço entre a base da etiqueta e o topo do número, em píxeis.
 *
 * *«Imediatamente acima»* é o contrato, e dois píxeis eram o menos que separa
 * duas linhas sem as colar - a quinze píxeis de glifo. Ele acompanha a escala,
 * porque um intervalo fixo numa composição do dobro cola as duas metades. Um valor maior faria as duas metades lerem-se como
 * duas coisas, que é exatamente o que a composição existe para evitar.
 */
export const COMBAT_TEXT_REFLECT_GAP_PX = 2 * COMBAT_TEXT_REFLECT_SCALE;

/** Quanto um número sobe ao longo da vida. */
export const COMBAT_TEXT_RISE_PX = 30;
/** Quanto tempo vive. */
export const COMBAT_TEXT_LIFE_MS = 580;
/** Onde um `+VALOR` nasce, acima do centro da nave. */
export const COMBAT_TEXT_HEAL_OFFSET_PX = 34;

/** Um milésimo de ponto de vida. A unidade em que uma cura viaja. */
export const HEAL_TEXT_MILLI_PER_UNIT = 1000;

/* -------------------------------------------------------------------------- */
/*  O leque: seis posições, uma só fórmula                                    */
/* -------------------------------------------------------------------------- */

/**
 * Quantas posições o leque percorre antes de repetir.
 *
 * Seis é o que a caça já usava para os números de DANO desde antes deste gate,
 * e a razão está escrita na cena que o inventou: *«simultaneous hits fan out
 * slightly so they never stack into one blob»*.
 */
export const COMBAT_TEXT_SPREAD_STEPS = 6;

/**
 * **O deslocamento da enésima posição do leque.**
 *
 * ## Porque isto saiu da cena para aqui
 *
 * Porque «o mesmo leque dos números de dano» tinha de ser verdade por
 * CONSTRUÇÃO. Copiar as duas linhas para o caminho da cura daria duas
 * implementações da mesma regra, e duas implementações de uma regra são uma
 * divergência à espera de alguém que corrija um arredondamento só numa delas.
 * Aqui há uma, e as três chamadas - dano na caça, cura na caça, cura no duelo -
 * leem-na.
 *
 * A aritmética é a que já existia, sem uma vírgula mudada: `x` abre um leque
 * simétrico de ±17,5 px em passos de 7, e `y` levanta em três degraus de 4 px
 * que se repetem duas vezes ao longo dos seis. O resultado é que dois números
 * consecutivos nunca nascem no mesmo ponto, e seis seguidos ocupam seis pontos
 * distintos.
 *
 * ## O que isto NÃO é
 *
 * Não é um efeito, não é aleatoriedade e não toca na simulação. É uma sequência
 * determinística de seis posições, contada por um índice que vive na cena.
 *
 * ## E serve o DANO, e só o dano
 *
 * A cura usou-o durante um gate e deixou de usar. O leque separa números que
 * nascem juntos, e a folha visual provou que separar não chega quando eles são
 * sete: o passo é de sete píxeis e o glifo mede cerca de dez. A cura passou a
 * ter um texto só por nave, somado - ver `heal-tally.ts` - portanto não há nada
 * para espalhar. Um número de dano continua a ser um por acerto, e para esse a
 * separação continua a ser a resposta certa.
 */
export function combatTextSpread(index: number): { readonly x: number; readonly y: number } {
  return { x: (index - 2.5) * 7, y: (index % 3) * -4 };
}

/* -------------------------------------------------------------------------- */
/*  O orçamento do pool, derivado                                             */
/* -------------------------------------------------------------------------- */

/**
 * **Os textos de DANO que a caça reservava antes deste gate. Dívida herdada.**
 *
 * Vinte e quatro, e **nunca foi derivado de nada**. Nem deste gate nem de
 * nenhum: apareceu como o tamanho que a caça reservava, e ficou.
 *
 * A caça mediu-o a desmentir-se: com o canhão Anomaly de nível 7 - vinte e um
 * projéteis por salva, a 3,4 salvas por segundo, cada acerto com o seu número a
 * viver 580 ms - o pool do dano chegou a **33** numa prova e a **54** noutra.
 * Duas salvas cabem folgadamente numa vida, portanto dezenas de números
 * coexistem com naturalidade e este 24 não descreve nada.
 *
 * **Continua aqui e continua por derivar**, deliberadamente: o dano não é deste
 * gate para mexer, e um número que ninguém consegue explicar é melhor visível do
 * que apagado. O que este gate faz é deixar de o usar como teto de coisa
 * nenhuma - ver `HEAL_TEXT_BUDGET`, que é derivado e vale sozinho.
 */
export const DAMAGE_TEXT_BUDGET = 24;

/**
 * **Quantos `+TOTAL` podem coexistir: um por nave** (decisão final).
 *
 * ## A conta mudou porque a regra mudou
 *
 * Enquanto cada cura nascia com o seu texto, o pior caso de um atacante eram os
 * tiques que cabem numa vida - `teto(580 / tique) + 1`, treze no duelo e trinta
 * e oito na caça. Vezes seis atacantes davam setenta e oito.
 *
 * Agora **uma nave tem no máximo um texto**: as curas seguintes somam-se ao que
 * já está no ecrã em vez de criarem outro. O pior caso deixou de depender do
 * tique e da duração, e passou a ser simplesmente quantas naves podem estar a
 * curar ao mesmo tempo.
 *
 * Isso é um número que o produto já sabe dizer, e não uma estimativa:
 * `pvpRosterSize('team_3v3')` é 6.
 */
export function healTextBudget(ships: number): number {
  return ships;
}

/**
 * O 3×3: seis naves, seis textos.
 *
 * Lido de `pvpRosterSize`, que é quem define quantos cascos uma sala tem. Um 6
 * escrito à mão aqui seria um segundo sítio a saber o tamanho de uma equipa.
 */
export const PVP_HEAL_TEXT_BUDGET = healTextBudget(pvpRosterSize('team_3v3'));

/** A caça: uma nave, um texto. */
export const HUNT_HEAL_TEXT_BUDGET = healTextBudget(1);

/** O pior caso da cura entre os modos: **6**, do 3×3. */
export const HEAL_TEXT_BUDGET = Math.max(PVP_HEAL_TEXT_BUDGET, HUNT_HEAL_TEXT_BUDGET);

/**
 * **Não há teto partilhado, e é essa a correção.**
 *
 * Houve um: `DAMAGE_TEXT_BUDGET + HEAL_TEXT_BUDGET`, primeiro 102 e depois 30.
 * Ele somava uma coisa derivada a uma coisa herdada e tratava a soma como se as
 * duas tivessem a mesma autoridade - e o resultado foi um teto que a caça
 * ultrapassou por causa do lado que ninguém tinha calculado.
 *
 * Os dois pools passaram a ser separados: o do dano fica exatamente como estava
 * antes deste gate, sem teto novo e sem asserção nova, e a cura tem o seu, com
 * {@link HEAL_TEXT_BUDGET} objetos e mais nenhum. Um número de dano nunca tira
 * um objeto à cura, e uma cura nunca tira um ao dano - não por disciplina, mas
 * porque já não há nada em comum entre eles.
 */

/**
 * **`+VALOR`: a vida que uma nave REALMENTE recuperou** (gate V28G.I).
 *
 * ## A mesma gramática do dano, com uma casa a mais
 *
 * Vírgula decimal, zeros finais aparados e a mesma escala curta - `mil`, `mi`,
 * `bi`, `tri` - que {@link formatCombatNumber} usa, e a partir de mil é ele
 * mesmo que escreve.
 *
 * A diferença é abaixo de mil: **três** casas em vez de duas. Não é uma segunda
 * gramática, é a mesma com a precisão que esta grandeza tem. O dano de um tiro
 * nunca é `0,004`; uma cura de um milésimo de ponto é um facto ordinário, e com
 * duas casas ela apareceria como `+0` - um número a dizer que a nave não
 * recuperou nada, quando recuperou.
 *
 * Três é exatamente a precisão do milli, que é a unidade em que a cura viaja.
 * Nem uma a menos, para não esconder; nem uma a mais, para não prometer uma
 * precisão que a autoridade não tem.
 *
 * O `+` é a única coisa que se acrescenta. Não há símbolo, ícone nem unidade: o
 * sinal já diz que a vida subiu, e o contexto - um número sobre a própria nave -
 * diz de quem.
 *
 * ## A soma acontece ANTES, em milli
 *
 * O chamador soma todas as curas do mesmo atacante no mesmo tique e entrega o
 * total. Em milli inteiro, para a soma ser exata; a divisão acontece aqui, uma
 * vez.
 *
 * ## `null` só quando não houve cura
 *
 * Zero, negativo ou não-finito. Uma cura POSITIVA aparece sempre, por pequena
 * que seja: não há limiar, não há supressão, e `+0` não é um resultado que esta
 * função saiba produzir.
 */
export function formatHealAmount(milli: number): string | null {
  if (!Number.isFinite(milli) || milli <= 0) {
    /*
     * A ÚNICA razão para não haver número: não houve cura.
     *
     * Uma cura positiva aparece sempre, por pequena que seja - é isso que a
     * terceira casa existe para garantir. Esconder uma seria dizer que a nave
     * não recuperou nada quando recuperou.
     */
    return null;
  }
  const points = milli / HEAL_TEXT_MILLI_PER_UNIT;
  if (Math.abs(points) >= THOUSAND) {
    /*
     * A partir de mil, a escala curta canónica - `mil`, `mi`, `bi`, `tri` - a
     * mesma que um número de dano usa. Uma nave com vida enorme cura valores
     * enormes, e um número de nove dígitos sobre um casco não se lê.
     */
    return `+${formatCombatNumber(points)}`;
  }
  /*
   * Abaixo de mil: até TRÊS casas, e nem uma a mais.
   *
   * Três é exatamente a precisão do milli, que é a unidade em que a cura viaja
   * - portanto `toFixed(3)` não arredonda coisa nenhuma, transcreve. Duas casas
   * transformariam `+0,004` em `+0` e escondiam uma cura real; quatro
   * prometeriam uma precisão que a autoridade não tem.
   *
   * Os zeros finais saem: `1,000` é `1`, e `1,800` é `1,8`.
   */
  return `+${trim(points.toFixed(3).replace('.', ','))}`;
}

/** `atual / máximo`, as printed on an enemy's bar. */
export function formatHealthPair(current: number, maximum: number): string {
  return `${formatCombatNumber(Math.max(0, current))} / ${formatCombatNumber(Math.max(0, maximum))}`;
}
